import { useMemo } from 'react';

import { useDashboardSegmentQuery } from '../api';
import { isDashboardResponseScopeMatch } from '../helpers/dashboardScope';
import { buildDashboardSegmentPage } from '../helpers/dashboardViewModel';
import type { EDashboardSegmentKey } from '../types/dashboard.types';

type TUseDashboardSegmentDataOptions = {
  selectedRootIds: string[];
  segmentKey: EDashboardSegmentKey;
  page: number;
  pageSize: number;
  enabled?: boolean;
};

export const useDashboardSegmentData = ({
  selectedRootIds,
  segmentKey,
  page,
  pageSize,
  enabled = true,
}: TUseDashboardSegmentDataOptions) => {
  const query = useDashboardSegmentQuery({
    rootIds: selectedRootIds,
    segmentKey,
    page,
    pageSize,
    enabled,
  });
  const responseMatchesSelection =
    isDashboardResponseScopeMatch(query.data?.scope, selectedRootIds) &&
    query.data?.segment.key === segmentKey &&
    query.data.segment.pageSize === pageSize &&
    (query.isPlaceholderData || query.data.segment.page === page - 1);
  const response = responseMatchesSelection ? query.data : undefined;
  const segmentPage = useMemo(() => (response ? buildDashboardSegmentPage(response) : undefined), [response]);

  return {
    segmentPage,
    isLoading: query.isLoading || (!response && query.isFetching),
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    refetch: query.refetch,
  };
};
